import type { StorageUsage } from '~/gql/storage/storage'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { Progress } from '~/components/ui/progress'
import { cn } from '~/lib/utils'
import { formatBytes, usagePercent } from '~/utils/bytes'

type Props = { usage?: StorageUsage }

/**
 * Ce que l'organisation occupe, à l'endroit où elle téléverse (#180).
 *
 * Deux affichages selon la formule, et c'est le coeur de l'issue : sur un compte
 * payant on MESURE sans plafonner (« 3,2 Go utilisés », pas de jauge), sur le
 * gratuit on affiche le rapport au plafond (« 3,2 Go / 10 Go »). Un plafond
 * qu'on ne découvre qu'au moment du refus n'est pas un plafond, c'est un mur :
 * la jauge est là pour que le refus n'arrive jamais par surprise.
 */
function StorageUsageCard({ usage }: Props) {
  const { t, i18n } = useTranslation()

  if (!usage) return null

  const used = formatBytes(usage.usedBytes, i18n.language)
  const capped = usage.quotaBytes != null

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {t('storage.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-2xl font-semibold tabular-nums">
            {capped
              ? t('storage.usedOfQuota', { used, quota: formatBytes(usage.quotaBytes!, i18n.language) })
              : used}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('storage.moduleCount', { count: usage.moduleCount })}
          </p>
        </div>

        {capped && (
          <>
            <Progress
              value={usagePercent(usage.usedBytes, usage.quotaBytes!)}
              aria-label={t('storage.usage.progress')}
              className={cn('h-2', usage.overQuota && '[&>div]:bg-destructive')}
            />
            <p className={cn('text-sm', usage.overQuota ? 'text-destructive' : 'text-muted-foreground')}>
              {usage.overQuota
                ? t('storage.overQuota')
                : t('storage.remaining', {
                    remaining: formatBytes(Math.max(0, usage.remainingBytes ?? 0), i18n.language),
                  })}
            </p>
          </>
        )}

        {/* Sur un compte payant, on dit explicitement qu'on ne plafonne pas :
            un chiffre seul laisserait croire qu'une limite se cache derrière. */}
        {!capped && (
          <p className="text-sm text-muted-foreground">{t('storage.noQuota')}</p>
        )}
      </CardContent>
    </Card>
  )
}

export default StorageUsageCard
